import { useTheme } from '../ThemeProvider'
import { TExtendedColorKey } from '../typings'
import { getColorValue } from '../utils'
import { useFgColorValue } from './useFgColorValue'
import { useStatusFgColorValue } from './useStatusFgColorValue'

type TOptions = {
  /**
   * any color key that can be used to get a color value from the theme
   * or some special values like `inherit` or `currentColor`,
   * this has the highest priority above other options
   */
  color?: TExtendedColorKey | `currentColor`
  /**
   * how contrasted the icon should be on its background,
   * this has the lowest priority if `color`/`status` options are provided
   */
  contrast?: FirstParam<typeof useFgColorValue>['contrast']
  /**
   * color key for common statuses that can have associated color (error-red, success-green, ...),
   * this has lower priority than `color` argument but higher than `contrast`
   */
  status?: FirstParam<typeof useStatusFgColorValue>['status']
}

/**
 * an almighty function that should always return the fg value that you need
 * if you don’t need special color & can make it with basic gray scales, prefer `useFgColorValue` instead
 *
 * returns a fg color based on provided options
 *  - if color theme key is provided, the value taken from the theme is returned
 *  - if special color value (inherit, currentColor, ...) is provided, it gets returned back
 *  - if color key that can be transformed to status is provided, themed status color value is returned
 *  - if status is provided, themed status color value is returned
 *  - if contrast is provided, fg value with that contrast level is returned
 *  - if nothing is provided, fg value for `normal` contrast level is returned
 *
 * ! requires BgColorProvider somewhere above – where the closest bg color is set
 */
export const useAnyFgColorValue = ({ color, contrast, status }: TOptions) => {
  const theme = useTheme()
  const colorStatus = getColorStatus(color)

  const valueForContrast = useFgColorValue({ contrast })
  const valueForColor = getColorValue(theme)(color ?? `specialColors.company`)
  const valueForStatus = useStatusFgColorValue({ status: colorStatus ?? status ?? `info` })

  if (colorStatus) return valueForStatus
  if (color) return valueForColor
  if (status) return valueForStatus

  return valueForContrast
}

// * HELPERS

// transform some special colors to statuses
// if these colors are used as fg colors, they will be themed
const getColorStatus = (
  color: TOptions['color'],
): Nullable<FirstParam<typeof useStatusFgColorValue>['status']> => {
  if (color === `specialColors.info`) return `info`
  if (color === `specialColors.error`) return `error`
  if (color === `specialColors.alert`) return `alert`
  if (color === `specialColors.success`) return `success`

  return null
}
